import React from 'react';
import {
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	type MessageDescriptor,
} from 'react-intl-next';
import {
	messages,
	commentedEventTypeMessages,
	createdEventTypeMessages,
	editedEventTypeMessages,
	publishedEventTypeMessages,
	updatedEventTypeMessages,
	viewedEventTypeMessages,
	defaultEventTypeMessages,
} from '../../../messages';

const SECONDS_IN_MIN = 60;
const SECONDS_IN_HR = SECONDS_IN_MIN * 60;
const SECONDS_IN_DAY = SECONDS_IN_HR * 24;

export type EventTypeMessages = {
	absolute: MessageDescriptor;
	relative: MessageDescriptor;
};

export const LOCAL_EVENT_TYPE_MESSAGES: Record<string, EventTypeMessages> = {
	commented: commentedEventTypeMessages,
	created: createdEventTypeMessages,
	edited: editedEventTypeMessages,
	published: publishedEventTypeMessages,
	updated: updatedEventTypeMessages,
	viewed: viewedEventTypeMessages,
};

const WithUpdatedLabel = (formattedDate: string) => {
	const i18nMessage = messages.common_updated_date_formatter;

	return <FormattedMessage {...i18nMessage} values={{ date: formattedDate }} />;
};

const recentEventLabel = (eventType?: string) => {
	const i18nMessage = eventType
		? LOCAL_EVENT_TYPE_MESSAGES[eventType] || defaultEventTypeMessages
		: defaultEventTypeMessages;

	return (formattedDate: string) => (
		<FormattedMessage {...i18nMessage.relative} values={{ date: formattedDate }} />
	);
};

const absoluteRecentEventLabel = (eventType?: string) => {
	const i18nMessage = eventType
		? LOCAL_EVENT_TYPE_MESSAGES[eventType] || defaultEventTypeMessages
		: defaultEventTypeMessages;
	return (formattedDate: string) => (
		<FormattedMessage {...i18nMessage.absolute} values={{ date: formattedDate }} />
	);
};

export function dateFormatter(
	lastModified: string,
	isRecentActivity?: boolean,
	eventType?: string,
) {
	const secondsSinceUpdate = Math.floor((Date.now() - new Date(lastModified).getTime()) / 1000);

	const totalDays = Math.floor(secondsSinceUpdate / SECONDS_IN_DAY);
	const totalHours = Math.floor(secondsSinceUpdate / SECONDS_IN_HR);
	const totalMinutes = Math.floor(secondsSinceUpdate / SECONDS_IN_MIN);
	if (secondsSinceUpdate > 1 && secondsSinceUpdate < 60) {
		return (
			<FormattedRelativeTime value={-secondsSinceUpdate} style="narrow" unit="second">
				{isRecentActivity ? recentEventLabel(eventType) : WithUpdatedLabel}
			</FormattedRelativeTime>
		);
	}

	if (totalMinutes >= 1 && totalMinutes < 60) {
		return (
			<FormattedRelativeTime value={-totalMinutes} style="narrow" unit="minute">
				{isRecentActivity ? recentEventLabel(eventType) : WithUpdatedLabel}
			</FormattedRelativeTime>
		);
	}

	if (totalHours >= 1 && totalHours < 24) {
		return (
			<FormattedRelativeTime value={-totalHours} style="narrow" unit="hour">
				{isRecentActivity ? recentEventLabel(eventType) : WithUpdatedLabel}
			</FormattedRelativeTime>
		);
	}

	if (totalDays >= 1 && totalDays < 8) {
		return (
			<FormattedRelativeTime value={-totalDays} style="narrow" unit="day">
				{isRecentActivity ? recentEventLabel(eventType) : WithUpdatedLabel}
			</FormattedRelativeTime>
		);
	}

	return (
		<FormattedDate year="numeric" month="short" day="2-digit" value={lastModified}>
			{isRecentActivity ? absoluteRecentEventLabel(eventType) : WithUpdatedLabel}
		</FormattedDate>
	);
}
